import type { CSSProperties, FC } from 'react';
import { Menu } from 'antd';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import type { InboxItem } from '@/services/database';
import { DropdownPanelMenu } from '@/shared/components/dropdowns/DropdownPanel';

import NotificationMenuItem from '../NotificationMenuItem';
import { notificationMenuItemClassName, notificationMenuItemStyle } from '../NotificationMenuItem/styles';

interface NotificationMenuItemListProps {
    items: readonly InboxItem[];
    onItemClick: (item: InboxItem) => Promise<void> | void;
    onMarkRead?: (item: InboxItem) => Promise<void> | void;
    selectedItemId?: string;
    showResourceId?: boolean;
    showStatusControls?: boolean;
}

/** Borderless notification menu items separated by semantic menu dividers. */
const NotificationMenuItemList: FC<NotificationMenuItemListProps> = ({
    items,
    onItemClick,
    onMarkRead,
    selectedItemId,
    showResourceId,
    showStatusControls,
}) => {
    const { token } = useAntDesignTheme();

    return (
        <DropdownPanelMenu selectable={false} style={{ paddingBlock: 0 }}>
            {items.flatMap((item, itemIndex) => {
                const selected = selectedItemId === item.id;
                const itemStyle: CSSProperties = {
                    ...notificationMenuItemStyle,
                    background: selected ? token.colorFillSecondary : undefined,
                    border: 0,
                };
                const menuItem = (
                    <Menu.Item
                        aria-current={selected ? 'true' : undefined}
                        className={notificationMenuItemClassName}
                        data-notification-menu-item='true'
                        key={item.id}
                        onClick={() => void onItemClick(item)}
                        style={itemStyle}
                    >
                        <NotificationMenuItem
                            item={item}
                            onMarkRead={onMarkRead}
                            showResourceId={showResourceId}
                            showStatusControls={showStatusControls}
                        />
                    </Menu.Item>
                );

                if (itemIndex === items.length - 1) {
                    return [menuItem];
                }

                return [
                    menuItem,
                    <Menu.Divider
                        className='notification-menu-divider'
                        key={`${item.id}:divider`}
                        style={{ borderColor: token.colorBorder, margin: 0 }}
                    />,
                ];
            })}
        </DropdownPanelMenu>
    );
};

export default NotificationMenuItemList;
export type { NotificationMenuItemListProps };
